import {
  Header,
  Layout,
  ProjectListing,
  SEO,
  Title,
  Wrapper
} from "components";

import ProjectCategories from "../components/ProjectListing/ProjectCategories";
import PropTypes from "prop-types";
import React from "react";
import { graphql } from "gatsby";
import styled from "react-emotion";
import website from "../../config/website";
import Img from 'gatsby-image';
import { Link } from 'gatsby';

const Hero = styled.section`
    background-color: ${props => props.theme.colors.darkBlue};
  `;

const HeroWrapper = styled(Wrapper)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.45rem;
`;

const Headline = styled.h3`
    color: ${props => props.theme.colors.white};
    line-height: 150%;
    width: 80%;
    margin-bottom: 0;
    @media (max-width: ${props => props.theme.breakpoints.m}) {
      width: 100%;
    }
  `;

const BreadCrumb = styled.ul`
  width: 20%;
  color: ${props => props.theme.colors.white};
  font-size: 0.75rem;
  text-align: right;
  margin: 0;
  li {
    display: inline;
  }
  li:not(:first-child):before {
    content:  " > ";
    margin: 0 0.75rem 0 0.75rem;
  }
  a {
    color: ${props => props.theme.colors.white};
    font-style: normal;
  }
  @media (max-width: ${props => props.theme.breakpoints.m}) {
    display: none;
  }
`;

const ProjectContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ProjectDescription = styled.div`
  display: flex; 
  flex-direction: column;
  width: 60%;
  padding-right: 3rem;
  p, li {
    font-size: 0.75rem;
    margin-bottom: 1rem;
  }
  @media (max-width: ${props => props.theme.breakpoints.s}) {
    width: 100%;
  }
  h1, h2, h3, h4, h5, h6 {
    margin-bottom: 0.5rem;
  }
`;

const ProjectContent = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 2rem;
  @media (max-width: ${props => props.theme.breakpoints.s}) {
    flex-direction: column-reverse;
  }
`;

const ProjectDetails = styled.div`
    width: 40%;
    svg {
      height: 1em;
      width: 0.375em;
      margin-right: 0.5rem;
      margin-left: 0.5rem;
      color: ${props => props.theme.colors.darkBlue};
    }
    strong {
      margin-right: 0.25rem;
      color: ${props => props.theme.colors.darkBlue};
    }
    ul {
      list-style: none;
    }
    @media (max-width: ${props => props.theme.breakpoints.s}) {
      width: 100%;
    }
    h1, h2, h3, h4, h5, h6 {
      margin-bottom: 0.5rem;
    }
    table {
      font-size: 0.75rem;
      ul {
        margin: 0;
        padding: 0;
      }
    }
    tr {
      line-height: 100%;
    }
`;

const Project = ({ data: { prismicProject, projects }, location }) => {
  const { data } = prismicProject;
  let projectListings = [];
  projects.edges.forEach(function (project) {
    if (project.node.uid !== prismicProject.uid) {
      // Exclude the current project from the listing
      projectListings.push(project);
    }
  });
  let projectCategories = false;
  if (data.categories[0].projectcategory) {
    projectCategories = data.categories.map(c => c.projectcategory.document[0].data);
  }

  return (
    <Layout>
      <SEO
        title={`${data.title.text} | ${website._title}`}
        pathname={location.pathname}
        article
      />
      <Header />
      <Hero>
        <HeroWrapper>
          {/* <Headline>
              {data.date} — {projectCategories && <ProjectCategories projectcategories={projectCategories} />}
            </Headline> */}
          <Headline>{data.title.text}</Headline>
          <BreadCrumb>
            <li><Link to='/'>Home</Link></li>
            <li>Projects</li>
          </BreadCrumb>
        </HeroWrapper>
      </Hero>
      <Wrapper>
        <ProjectContainer>
          <Img fluid={data.headline_image.localFile.childImageSharp.fluid} style={{ width: '100%', height: '50%' }} />
          <ProjectContent>
            <ProjectDescription>
              <h4>Description</h4>
              <div
                dangerouslySetInnerHTML={{
                  __html: data.project_description.html
                }} />
            </ProjectDescription>
            <ProjectDetails>
              <h4>Project Details</h4>
              <table>
                <tbody>
                  <tr>
                    <td style={{ width: '25%' }}><strong>Client:</strong></td><td>{data.client.text}</td>
                  </tr>
                  <tr>
                    <td style={{ width: '25%' }}><strong>Solutions:</strong></td><td>{data.solutions.text}</td>
                  </tr>
                  <tr>
                    <td style={{ width: '25%', verticalAlign: 'top' }}><strong>Services:</strong></td><td style={{ paddingBottom: 0 }} dangerouslySetInnerHTML={{ __html: data.services.html }}></td>
                  </tr>
                  <tr>
                    <td style={{ width: '25%' }}><strong>Timeframe:</strong></td><td>{data.timeframe.text}</td>
                  </tr>
                </tbody>
              </table>
            </ProjectDetails>
          </ProjectContent>
        </ProjectContainer>
        <Title style={{ marginTop: "2rem" }}>Other Projects</Title>
        <ProjectListing projects={projectListings.splice(0, 3)} />
      </Wrapper>
    </Layout>
  );
};

export default Project;

Project.propTypes = {
  data: PropTypes.shape({
    prismicProject: PropTypes.object.isRequired
  }).isRequired,
  location: PropTypes.object.isRequired
};

// The typenames come from the slice names
// If this doesn't work for you query for __typename in body {} and GraphiQL will show them to you

export const pageQuery = graphql`
    query ProjectBySlug($uid: String!) {
      prismicProject(uid: { eq: $uid }) {
        uid
        data {
          title {
            text
          }
          date(formatString: "DD.MM.YYYY")
          headline_image {
            localFile {
              childImageSharp {
                fluid (quality: 100) {
                  aspectRatio
                  src
                  srcSet
                  sizes
                }
                fixed {
                  src
                  srcSet
                  width
                  height
                }
              }
            }
          }
          client {
            text
          }
          solutions {
            text
          }
          services {
            html
          }
          project_description {
            html
          } 
          timeframe {
            text
          }
          summary {
            text
          }
          categories {
            projectcategory {
              document {
                data {
                  name
                  category_colour
                }
              }
            }
          }
        }
      }
      projects: allPrismicProject(
        limit: 4
        sort: { fields: [data___date], order: DESC }
      ) {
        edges {
          node {
            uid
            data {
              title {
                text
              }
              date(formatString: "DD.MM.YYYY")
              thumbnail {
                localFile {
                  childImageSharp {
                    fluid {
                      aspectRatio
                      src
                      srcSet
                      sizes
                    }
                    fixed {
                      src
                      srcSet
                      width
                      height
                    }
                  }
                }
              }
              client {
                text
              }
              summary {
                text
              }
              categories {
                projectcategory {
                  document {
                    data {
                      name
                      category_colour
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;
